import { Insight } from '../../../components/office';
import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useMemo, useState } from 'react';
import {
  Target, ChevronLeft, ChevronRight, Copy, Sparkles, Loader2,
  TrendingUp, TrendingDown, Minus, RefreshCw, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { reportsIntelApi } from '@verlo/shared/services/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { useAuth } from '@verlo/shared/contexts/AuthContext';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

const STATUS_STYLES = {
  good:    { pill: 'bg-signal-ok-tint text-signal-ok border-signal-ok-line', dot: 'bg-signal-ok-line', icon: CheckCircle2 },
  warn:    { pill: 'bg-signal-warn-tint text-signal-warn border-signal-warn-line',       dot: 'bg-signal-warn-line',   icon: AlertTriangle },
  bad:     { pill: 'bg-signal-stop-tint text-signal-stop border-signal-stop-line',          dot: 'bg-signal-stop-line',    icon: AlertTriangle },
  neutral: { pill: 'bg-surface-2 text-ink-soft border-hairline',       dot: 'bg-ink-faint',   icon: Minus },
};

const fmt = (kpi, v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (kpi.format === 'currency') return `£${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (kpi.format === 'percent')  return `${Number(v).toFixed(1)}%`;
  if (kpi.format === 'int')      return Number(v).toLocaleString('en-GB');
  return String(v);
};

const BudgetPage = () => {
  const now = new Date();
  // `site` is required by GET/PUT /reports/budget, so it has to name a real site.
  // It used to be one of three hardcoded demo sites (`report_sites`, deleted in S1-1);
  // the roster now comes from GET /reports/sites, which lists only sites this user
  // may read. Empty until that resolves — the loaders wait for it.
  const [siteOptions, setSiteOptions] = useState([]);
  const [site, setSite] = useFollowedSite('');
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [budget, setBudget] = useState(null);
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);  // kpi_key currently being saved
  const [draft, setDraft] = useState({});      // local-edit map { kpi_key: { monthly_target, weekly_targets } }
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('reports.edit_budget');

  const load = async () => {
    setLoading(true);
    try {
      const [b, v] = await Promise.all([
        reportsIntelApi.getBudget(site, year, month),
        reportsIntelApi.getBudgetVariance(site, year, month),
      ]);
      setBudget(b.data);
      setVariance(v.data);
      // Reset draft to match server state
      const d = {};
      b.data.rows.forEach((r) => {
        d[r.key] = { monthly_target: r.monthly_target, weekly_targets: [...(r.weekly_targets || [])] };
      });
      setDraft(d);
      } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load budgets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reportsIntelApi
      .listSites()
      .then((r) => {
        const rows = r.data.sites || [];
        setSiteOptions(rows.map((x) => ({ value: x.id, label: x.name })));
        // A budget is per site: with none picked in the header, the first site.
        if (!site && rows[0]?.id) setSite(rows[0].id);
      })
      .catch(() => toast.error('Could not load sites'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (site) load(); /* eslint-disable-next-line */ }, [site, year, month]);

  const stepMonth = (delta) => {
    let m = month + delta; let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const onDraft = (key, field, value, weekIdx) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: { ...prev[key] } };
      if (field === 'monthly') next[key].monthly_target = value === '' ? null : Number(value);
      if (field === 'week') {
        const arr = [...(next[key].weekly_targets || [])];
        arr[weekIdx] = value === '' ? null : Number(value);
        next[key].weekly_targets = arr;
      }
      return next;
    });
  };

  const saveRow = async (kpi) => {
    if (!canEdit) return;
    const local = draft[kpi.key] || {};
    if (local.monthly_target === null || local.monthly_target === undefined || Number.isNaN(local.monthly_target)) {
      toast.error('Set a monthly target before saving');
      return;
    }
    setSaving(kpi.key);
    try {
      await reportsIntelApi.upsertBudget({
        site, year, month,
        kpi_key: kpi.key,
        monthly_target: local.monthly_target,
        weekly_targets: local.weekly_targets || null,
      });
      toast.success(`${kpi.label} budget saved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const copyLastMonth = async () => {
    if (!canEdit) return;
    try {
      const r = await reportsIntelApi.copyBudgetFromLastMonth(site, year, month);
      if (r.data.copied === 0) {
        toast.info(r.data.reason || 'Nothing to copy');
      } else {
        toast.success(`Copied ${r.data.copied} KPIs from ${r.data.source}${r.data.skipped ? ` (skipped ${r.data.skipped} already set)` : ''}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copy failed');
    }
  };

  // Summary stats for the hero
  const summary = useMemo(() => {
    if (!variance) return { setCount: 0, good: 0, warn: 0, bad: 0 };
    const setCount = variance.rows.filter((r) => r.monthly_target !== null).length;
    const good = variance.rows.filter((r) => r.status === 'good').length;
    const warn = variance.rows.filter((r) => r.status === 'warn').length;
    const bad  = variance.rows.filter((r) => r.status === 'bad').length;
    return { setCount, good, warn, bad };
  }, [variance]);

  return (
    <ReportsLayout>
      <div data-testid="budget-page">
        {/* Page header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[13px] text-ink-soft mt-1">
              Monthly budgets per KPI per site, optional per-week overrides, and a Claude-powered variance brief on demand.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={site} onValueChange={setSite}>
              <SelectTrigger className="w-[230px] h-9 text-[12px]" data-testid="budget-site-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {siteOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1 bg-white border border-hairline rounded-md h-9 px-1" data-testid="budget-month-stepper">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => stepMonth(-1)} data-testid="budget-prev-month">
                <ChevronLeft size={14} />
              </Button>
              <div className="text-[12px] font-semibold text-ink min-w-[120px] text-center font-mono-display">
                {MONTH_NAMES[month - 1]} {year}
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => stepMonth(1)} data-testid="budget-next-month">
                <ChevronRight size={14} />
              </Button>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={load} data-testid="budget-refresh">
              <RefreshCw size={13} />
              Refresh
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLastMonth} data-testid="budget-copy-last-month">
                <Copy size={13} />
                Copy last month
              </Button>
            )}
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-4 gap-3 mb-5" data-testid="budget-summary-tiles">
          <SummaryTile label="KPIs budgeted" value={`${summary.setCount}/8`} hint="of 8 budget-eligible KPIs" tone="slate" testid="tile-set" />
          <SummaryTile label="On or ahead" value={summary.good} hint="green status" tone="emerald" testid="tile-good" />
          <SummaryTile label="At risk" value={summary.warn} hint="within 5% of breach" tone="amber" testid="tile-warn" />
          <SummaryTile label="Missing" value={summary.bad} hint="more than 5% behind" tone="rose" testid="tile-bad" />
        </div>

        <Insight
          key={`${site}:${year}:${month}`}
          read={() => reportsIntelApi.explainBudget({ site, year, month })}
          headline="Why are we missing budget?"
          purpose="A read on the worst breaches and the single highest-impact action this week."
          className="mb-5"
          testId="budget-explain"
        />

        {/* Variance + budget table */}
        <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="budget-table">
          <div className="px-5 py-3 border-b border-hairline bg-surface-2/60 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-ink">
              {MONTH_NAMES[month - 1]} budgets · {variance ? `${variance.month_progress_pct}% through the month` : '—'}
            </div>
            <div className="text-[12.5px] text-ink-soft">
              {budget?.weeks_in_month ? `${budget.weeks_in_month} ISO weeks · weekly cells override the monthly target for that week` : ''}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-white">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft w-[140px]">KPI</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-ink-soft w-[110px]">Monthly target</th>
                  {Array.from({ length: budget?.weeks_in_month || 5 }).map((_, i) => (
                    <th key={i} className="text-right px-3 py-2.5 font-semibold text-ink-faint w-[80px]">W{i + 1}</th>
                  ))}
                  <th className="text-right px-4 py-2.5 font-semibold text-ink-soft w-[110px]">Actual MTD</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-ink-soft w-[100px]">Variance</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft w-[90px]">Status</th>
                  {canEdit && <th className="px-3 py-2.5 w-[70px]"></th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="px-4 py-8 text-center text-ink-faint">Loading…</td></tr>
                )}
                {!loading && variance && variance.rows.map((r) => {
                  const local = draft[r.key] || {};
                  const styles = STATUS_STYLES[r.status] || STATUS_STYLES.neutral;
                  const Icon = styles.icon;
                  const weeks = budget?.weeks_in_month || 5;
                  const VarianceTrend = r.variance_abs === null
                    ? Minus
                    : (r.variance_abs >= 0 ? TrendingUp : TrendingDown);
                  return (
                    <tr key={r.key} className="border-t border-hairline hover:bg-hover-tint/40" data-testid={`budget-row-${r.key}`}>
                      <td className="px-4 py-2.5 font-medium text-ink">{r.label}</td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit ? (
                          <Input
                            type="number"
                            value={local.monthly_target ?? ''}
                            onChange={(e) => onDraft(r.key, 'monthly', e.target.value)}
                            placeholder="—"
                            className="h-8 w-28 text-right font-mono-display text-[12px]"
                            data-testid={`budget-input-monthly-${r.key}`}
                          />
                        ) : (
                          <div className="font-mono-display text-ink-soft">{fmt(r, r.monthly_target)}</div>
                        )}
                      </td>
                      {Array.from({ length: weeks }).map((_, i) => (
                        <td key={i} className="px-3 py-2.5 text-right">
                          {canEdit ? (
                            <Input
                              type="number"
                              value={local.weekly_targets?.[i] ?? ''}
                              onChange={(e) => onDraft(r.key, 'week', e.target.value, i)}
                              placeholder="—"
                              className="h-8 text-right font-mono-display text-[12.5px] text-ink-soft"
                              data-testid={`budget-input-week-${r.key}-${i}`}
                            />
                          ) : (
                            <div className="font-mono-display text-[12.5px] text-ink-soft">{fmt(r, local.weekly_targets?.[i])}</div>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-right font-mono-display text-ink font-semibold">
                        {fmt(r, r.actual_value)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1 font-mono-display text-[12px]">
                          <VarianceTrend size={12} className={r.variance_abs === null ? 'text-ink-faint' : (r.variance_abs >= 0 ? 'text-signal-ok' : 'text-signal-stop')} />
                          {r.variance_pct !== null ? `${r.variance_pct > 0 ? '+' : ''}${r.variance_pct}%` : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 text-[12.5px] font-semibold px-2 py-0.5 rounded-md border ${styles.pill}`} data-testid={`budget-status-${r.key}`}>
                          <Icon size={11} />
                          {r.status}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2.5 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={saving === r.key}
                            onClick={() => saveRow(r)}
                            className="h-7 px-2 text-[12.5px] text-verlo hover:bg-signal-info-tint"
                            data-testid={`budget-save-${r.key}`}
                          >
                            {saving === r.key ? <Loader2 size={11} className="animate-spin" /> : 'Save'}
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 border-t border-hairline text-[12.5px] text-ink-faint flex items-center justify-between">
            <span>{canEdit ? 'Edits stay local until you click Save per row.' : 'Read-only. Changing it needs Budget edit permission.'}</span>
            {variance?.day_of_month && (
              <span className="font-mono-display">Day {variance.day_of_month} of {variance.days_in_month}</span>
            )}
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

const SummaryTile = ({ label, value, hint, tone, testid }) => {
  const toneStyles = {
    slate:   'bg-white border-hairline text-ink',
    emerald: 'bg-signal-ok-tint/40 border-signal-ok-tint text-signal-ok',
    amber:   'bg-signal-warn-tint/40 border-signal-warn-tint text-signal-warn',
    rose:    'bg-signal-stop-tint/40 border-signal-stop-tint text-signal-stop',
  };
  return (
    <div className={`border rounded-lg p-4 ${toneStyles[tone] || toneStyles.slate}`} data-testid={`budget-${testid}`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="font-mono-display text-3xl font-medium mt-1.5 tabular-nums">{value}</div>
      <div className="text-[12.5px] opacity-60 mt-0.5">{hint}</div>
    </div>
  );
};

export default BudgetPage;
